import {Image, Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {atoms as a, useTheme} from '#/alf'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import {SubtleHover} from '#/components/SubtleHover'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {useTrendLabels} from '../data/labels'
import {type Trend} from '../data/trends'
import {getVoicesDisplay, useTrendSearchPress} from '../util'
import {TrendRankHeader} from './TrendRankPill'

/**
 * Compact prominence trend card.
 *
 * Mirrors Figma node `384:53729` in file `C8J3bA4ThNVCG0vzlHeI16` (Enneagram —
 * the design lives in this file while the main Trends file rate-limit is in
 * effect). Used when `section.cardStyle === 'compact'`.
 *
 * Single-tap zone, no CTA — the whole card is the affordance to drill into the
 * trend detail. Renders: title, description, avatar stack with active voices
 * count + category label. No hero image, no metric pills, no inline posts.
 */
export function CompactTrendCard({
  trend,
  rankLabel,
}: {
  trend: Trend
  /** When set (Top trends #3–5), shows the rank pill + category header at top;
   *  the bottom row then drops the category and shows avatars + voices only. */
  rankLabel?: string
}) {
  const t = useTheme()
  const {_} = useLingui()
  const goToTrendSearch = useTrendSearchPress()
  const {getCategoryLabel} = useTrendLabels()
  const hover = useInteractionState()
  const press = useInteractionState()

  const visibleAvatars = trend.participantAvatars.slice(0, 5)
  const voices = getVoicesDisplay(trend.metrics.activeVoicesCount)

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={_(msg`Explore trend: ${trend.title}`)}
      accessibilityHint={_(msg`Searches for this trend`)}
      onPress={() => goToTrendSearch(trend)}
      onHoverIn={hover.onIn}
      onHoverOut={hover.onOut}
      onPressIn={press.onIn}
      onPressOut={press.onOut}
      style={[a.w_full, a.relative, a.gap_md, a.px_lg, a.py_2xl]}>
      <SubtleHover hover={hover.state || press.state} />
      {rankLabel && (
        <TrendRankHeader
          rankLabel={rankLabel}
          categoryLabel={getCategoryLabel(trend.category)}
        />
      )}
      <View style={[a.gap_sm]}>
        <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
          {trend.title}
        </Text>
        <Text style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
          {trend.description}
        </Text>
      </View>

      <View style={[a.flex_row, a.align_center, a.gap_sm]}>
        {voices.showVoices && visibleAvatars.length > 0 && (
          <AvatarStack avatars={visibleAvatars} />
        )}
        <View style={[a.flex_row, a.align_center, a.gap_xs, a.flex_1]}>
          {voices.showVoices && (
            <Text
              style={[a.text_xs, a.font_medium, t.atoms.text_contrast_medium]}>
              {voices.showCount &&
                `${formatCount(trend.metrics.activeVoicesCount ?? 0)} `}
              {_(msg`active voices`)}
            </Text>
          )}
          {/* In the ranked block the category sits in the top header instead. */}
          {!rankLabel && (
            <>
              {voices.showVoices && (
                <View
                  style={[
                    a.rounded_full,
                    {
                      width: 4,
                      height: 4,
                      backgroundColor: t.atoms.text_contrast_medium.color,
                      opacity: 0.97,
                    },
                  ]}
                />
              )}
              <Text
                style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
                {getCategoryLabel(trend.category)}
              </Text>
            </>
          )}
        </View>
      </View>
    </Pressable>
  )
}

function AvatarStack({
  avatars,
}: {
  avatars: {did: string; handle: string; avatarUrl: string}[]
}) {
  const ss = useSemanticTokens()
  return (
    <View style={[a.flex_row, a.align_center]}>
      {avatars.map((avatar, i) => (
        <Image
          key={avatar.did}
          accessibilityIgnoresInvertColors
          source={{uri: avatar.avatarUrl}}
          style={[
            a.rounded_full,
            {
              width: 24,
              height: 24,
              marginLeft: i === 0 ? 0 : -8,
              borderWidth: 1,
              borderColor: 'rgba(0,0,0,0.2)',
              backgroundColor: ss.bg.contrast_25,
            },
          ]}
        />
      ))}
    </View>
  )
}

/* ---------- formatting + labels ---------- */

function formatCount(n: number): string {
  // Non-breaking-style thousands separator matches the Figma's number
  // formatting ("2 847").
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}
